import React, { useState } from 'react';
import { RcFile } from 'antd/lib/upload';
import { useSetRecoilState } from 'recoil';

import DependenciesModal from '../DependenciesModal';
import ProgressPanel from '../ProgressPanel';
import { ExchangeDependency, ExchangeObjectType, FolderModel } from '../../../gamma-api';
import { defaultApi } from '../../apis';
import IbIcon from '../common/IbIcon';
import { alertsSelectorAdd } from '../../recoil';
import { getTranslateFunc } from '../../utils/stringUtil';
import FileUpload from '../common/FileUpload';

import { buildDependenciesFile, hasActiveDependencies } from './utils';

interface IFolderImportProps {
  folderId?: string;
  onFinish?: (folder: FolderModel) => Promise<void>;
}

const FolderImport: React.FC<IFolderImportProps> = ({ folderId, onFinish, children }) => {
  const translate = getTranslateFunc('Folder: Import:');

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [currentFiles, setCurrentFiles] = useState<RcFile[]>([]);
  const [currentDependencies, setCurrentDependencies] = useState<ExchangeDependency[]>([]);

  const [dependenciesModalVisible, setDependenciesModalVisible] = useState(false);
  const [progressPanelVisible, setProgressPanelVisible] = useState(false);

  const importFolder = async (file: RcFile, dependencies: ExchangeDependency[]) => {
    const response = folderId
      ? await defaultApi.importFolderUpdateForm(folderId, file, buildDependenciesFile(dependencies), folderId)
      : await defaultApi.importFolderCreateForm(file, buildDependenciesFile(dependencies), folderId);
    onFinish && (await onFinish(response.data.folder));
  };

  const onFileUpload = async (files: RcFile[]) => {
    setProgressPanelVisible(true);
    try {
      const file = files[0];
      const response = folderId
        ? await defaultApi.inspectFolderUpdateForm(folderId, file, folderId)
        : await defaultApi.inspectFolderCreateForm(file, folderId);
      const dependencies = response.data.dependencies;
      if (hasActiveDependencies(dependencies, 1)) {
        setCurrentFiles(files);
        setCurrentDependencies(dependencies);
        setDependenciesModalVisible(true);
      } else {
        await importFolder(file, dependencies);
      }
    } catch (e) {
      addAlert({
        content: translate('Error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
      return;
    } finally {
      setProgressPanelVisible(false);
    }
    return false;
  };

  const onImportFinish = async () => {
    setDependenciesModalVisible(false);
    setProgressPanelVisible(true);
    try {
      await importFolder(currentFiles[0], currentDependencies);
    } catch (e) {
      addAlert({
        content: translate('Error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
      return;
    } finally {
      setProgressPanelVisible(false);
      setCurrentDependencies([]);
      setCurrentFiles([]);
    }

    addAlert({
      content: translate('Success'),
      type: 'success',
    });
  };

  const onImportCancel = async () => {
    setDependenciesModalVisible(false);
    setCurrentDependencies([]);
    setCurrentFiles([]);
  };

  const onDependenciesChange = (dependencies: ExchangeDependency[]) => {
    setCurrentDependencies(dependencies);
  };

  return (
    <>
      <FileUpload noButton accept=".zip" disabled={false} multiple={false} onUpload={onFileUpload}>
        {children}
      </FileUpload>
      <DependenciesModal
        dependencies={currentDependencies}
        entityId={folderId}
        entityType={ExchangeObjectType.Folder}
        folderId={folderId}
        visible={dependenciesModalVisible}
        onCancel={onImportCancel}
        onChange={onDependenciesChange}
        onFinish={onImportFinish}
      />
      <ProgressPanel.FolderImport visible={progressPanelVisible} />
    </>
  );
};

export default FolderImport;
